<!--suppress ALL -->
<script lang="ts">
  import {Component, Prop, Extend, Vue, Watch} from 'annotation';
  import {Line} from 'vue-chartjs';
  import {ChartColors, ChartFonts} from '@/config/ChartTheme';
  import {cloneDeep} from 'lodash';

  @Component
  export default class ModernRevenueChart extends Vue {

    @Extend
    public extend() {
      return Line
    }

    @Prop({default: []})
    public labels: Array<string>;

    @Prop({required: true})
    public datas: Array<any>;

    @Prop({default: {}})
    public options: Object;

    public mounted() {
      // Ensure specific DOM availability for canvas context
      this.$nextTick(() => {
        this.createChart();
      });
    }

    @Watch('datas')
    public onDatasChanged() {
      this.createChart();
    }

    /**
     * Creates vertical gradients for a specific color set.
     * Fade from solid (top) to transparent (bottom).
     */
    private createGradient(ctx: CanvasRenderingContext2D, colorStart: string, colorEnd: string) {
      const gradient = ctx.createLinearGradient(0, 0, 0, 400);
      gradient.addColorStop(0, colorStart);   // Solid at top
      // Make the bottom completely transparent for a nice fade
      gradient.addColorStop(0.8, 'rgba(255, 255, 255, 0)'); 
      return gradient;
    }

    // Generate data on-demand knowing ctx exists
    public generateChartData(ctx: CanvasRenderingContext2D) {
      const localDatasets = cloneDeep(this.dataSetsWithGradients(ctx));
      return {
        labels: this.labels,
        datasets: localDatasets
      };
    }

    private dataSetsWithGradients(ctx: CanvasRenderingContext2D) {
       return this.datas.map((dataset: any) => {
         let gradient;
         let borderColor;
         
         if (dataset.backgroundColor === ChartColors.info) {
           borderColor = ChartColors.info;
           gradient = this.createGradient(ctx, 'rgba(5, 150, 105, 0.1)', ChartColors.infoLight); // Emerald Ghost
         } else if (dataset.backgroundColor === ChartColors.danger) {
           borderColor = ChartColors.danger;
           gradient = this.createGradient(ctx, 'rgba(239, 68, 68, 0.1)', ChartColors.dangerLight); // Red Ghost
         } else if (dataset.backgroundColor === ChartColors.success) {
            borderColor = ChartColors.success;
            gradient = this.createGradient(ctx, 'rgba(16, 185, 129, 0.1)', ChartColors.successLight); // Green Ghost
         } else if (dataset.backgroundColor === ChartColors.warning) {
            borderColor = ChartColors.warning;
            gradient = this.createGradient(ctx, 'rgba(245, 158, 11, 0.1)', ChartColors.warningLight); // Amber Ghost
         } else {
            borderColor = ChartColors.primary;
            // Default Primary Gradient
            gradient = this.createGradient(ctx, 'rgba(5, 150, 105, 0.1)', 'rgba(5, 150, 105, 0)');
         }

         return {
           ...dataset,
           borderColor: borderColor,
           backgroundColor: gradient,
           fill: true,
           pointRadius: 4, // Visible dots
           pointHoverRadius: 6,
           pointHitRadius: 30,
           pointBackgroundColor: '#ffffff',
           pointBorderColor: borderColor,
           pointBorderWidth: 2,
           lineTension: 0.4, // Smooth curve
           borderWidth: 3,
           shadowColor: borderColor,
           shadowBlur: 10,
           shadowOffsetY: 4
         };
       });
    }

    public createChart() {
      const canvas = this.$refs.canvas as HTMLCanvasElement;
      if (!canvas) {
         return; 
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const chartData = this.generateChartData(ctx);

      // Modern Default Options
      const defaultOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            fontFamily: ChartFonts.family,
            fontColor: ChartColors.text,
            usePointStyle: true,
            padding: 20
          }
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false, // Clean Vertical Rhythm
              drawBorder: false
            },
            ticks: {
              fontFamily: ChartFonts.family,
              fontColor: ChartColors.text,
              padding: 10
            }
          }],
          yAxes: [{
            gridLines: {
              borderDash: [5, 5],
              color: 'rgba(200, 200, 200, 0.2)', // Lighter Grid
              drawBorder: false,
              zeroLineColor: 'transparent'
            },
            ticks: {
              fontFamily: ChartFonts.family,
              fontColor: ChartColors.text,
              padding: 10,
              callback: function (value: any) {
                 const val = parseFloat(value);
                 if (val === 0) return 'Rp. 0';
                 if (val >= 1000000000) return 'Rp. ' + (val / 1000000000).toFixed(1).replace(/\.0$/, '') + ' M';
                 if (val >= 1000000) return 'Rp. ' + (val / 1000000).toFixed(1).replace(/\.0$/, '') + ' Jt';
                 if (val >= 1000) return 'Rp. ' + (val / 1000).toFixed(0) + ' Rb';
                 return 'Rp. ' + val.toLocaleString('id-ID');
              }
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false, // Show tooltip when hovering anywhere on the x-axis
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleFontColor: '#1e293b',
          bodyFontColor: '#475569',
          borderColor: '#e2e8f0',
          borderWidth: 1,
          cornerRadius: 8,
          xPadding: 16,
          yPadding: 12,
          displayColors: true,
          callbacks: {
             title: function (tooltipItem: any, data: any) {
               return data['labels'][tooltipItem[0]['index']];
             },
             label: function (tooltipItem: any, data: any) {
                let label = data.datasets[tooltipItem.datasetIndex].label || '';
                if (label) {
                    label += ': ';
                }
                const val = tooltipItem.yLabel;
                const formattedVal = val >= 1000 ? 'Rp ' + val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") : 'Rp ' + val;
                return label + formattedVal;
             },
             labelTextColor: function(tooltipItem: any, chart: any) {
                // Return the dataset color for the text
                return chart.config.data.datasets[tooltipItem.datasetIndex].borderColor;
             }
          }
        },
        hover: {
          mode: 'nearest',
          intersect: true
        }
      };

      const finalOptions = Object.assign({}, defaultOptions, this.options);

      (this as any).renderChart(chartData, finalOptions);
    }
  }
</script>
